import React from "react";
import {
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  withStyles
} from "@material-ui/core";
import { isEmpty } from "lodash";
import locale from "@bonliva-crm/locale";
import {
  Customer,
  CustomerPicker_CustomerCardFragment,
  CustomerSelector_Search as search,
  CustomerType
} from "@graphql";
import { Classes, ThemeType } from "@theme";
import { PrimaryButton } from "@components";
import CustomerCard from "@customers/CustomerCard/CustomerCard";
import CustomerSelectorInput, {
  CustomerSelectorInputProps
} from "./CustomerSelectorInput";
import { Value } from "../base/utils";
import VirtualSelector from "../base/VirtualSelector";
import { SlimVirtualSelectorValue } from "../base/SlimVirtualSelectorValue";
import {
  CustomerSelectorStore,
  initialFilter,
  SearchFilter
} from "./CustomerSelectorStore";
import { CustomerSelectorFilter } from "./CustomerSelectorFilter";
import { SortOption } from "@search";

export interface CustomerSelectorBaseProps {
  classes: Classes<typeof styles>;
  clearable?: boolean; // true by default
  disabled?: boolean;
  hideButton?: boolean;
  disabledCustomerTypeOptions?: CustomerType[];
  disabledFilterFields?: Array<keyof SearchFilter>;
  error?: string;
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  initialFilter?: Partial<SearchFilter>;
  initialValues?: CustomerPicker_CustomerCardFragment[];
  label?: React.ReactNode;
  shrink?: boolean;
  onSearchClose?: () => void;
  open?: boolean;
  renderControl?: (
    handleClick: (e: React.SyntheticEvent<any>) => void
  ) => React.ReactNode;
  required?: boolean;
  slim?: boolean;
}

interface SingleProps extends CustomerSelectorBaseProps {
  multi?: false;
  value: Value | null;
  onChange: (v: CustomerPicker_CustomerCardFragment | null) => void;
}

interface MultiProps extends CustomerSelectorBaseProps {
  multi: true;
  value: Value[];
  onChange: (v: CustomerPicker_CustomerCardFragment[]) => void;
  onRemoveItem?: (id: string) => void;
}

type Props = MultiProps | SingleProps;

class CustomerSelector extends React.Component<Props> {
  static defaultProps: Partial<Props> = {
    clearable: true,
    disabledFilterFields: [],
    label: locale.SelectSomething(locale.Customers)
  };

  store: CustomerSelectorStore;

  constructor(props: Props) {
    super(props);

    const storeProps = {
      multi: props.multi,
      initialFilter: { ...initialFilter, ...props.initialFilter },
      fetch: () =>
        search
          .func({ filter: this.store.queryFilter }, { fetchPolicy: "network-only" })
          .then((result) => result.data?.customersPicker?.items),

      fetchMore: ({ limit, skip }) =>
        search
          .func(
            {
              filter: this.store.queryFilter,
              skip,
              limit
            },
            { fetchPolicy: "network-only" }
          )
          .then((result) => result.data?.customersPicker?.items),
      onChange: this.props.onChange,
      onSearchClose: this.props.onSearchClose
    };

    this.store =
      props.multi === true
        ? new CustomerSelectorStore({
            ...storeProps,
            multi: true,
            onChange: props.onChange,
            shouldFetch: (f) => f.text?.length > 1 || f.customerNumber?.length > 0
          })
        : new CustomerSelectorStore({
            ...storeProps,
            multi: false,
            onChange: props.onChange,
            shouldFetch: (f) => f.text?.length > 1 || f.customerNumber?.length > 0
          });

    (window as any).customerSelectorStore = this.store;

    this.store.setFilter({ ...initialFilter, ...props.initialFilter });
  }

  componentDidMount() {
    this.props.open && this.store.openSearchDialog();
  }

  componentDidUpdate(prevProps: Props) {
    !prevProps.open && this.props.open && this.store.openSearchDialog();
    prevProps.open && !this.props.open && this.store.closeSearchDialog();
  }

  private getSortOptions = (): SortOption => {
    const filter = this.store ? this.store.filter : this.props.initialFilter;
    return filter.sort;
  };

  handleInputClick = (e: React.SyntheticEvent<any>) => {
    if (this.store.isSearchOpen) {
      return;
    }

    this.store.openSearchDialog();
  };

  defaultRenderControl = () => {
    const { classes, value, multi, slim, shrink } = this.props;

    const inputProps: CustomerSelectorInputProps = {
      clearable: this.props.clearable,
      disabled: this.props.disabled,
      onClear: this.store.clear,
      onClick: this.handleInputClick,
      removeItem: (id) => {
        if (this.props.multi) {
          this.props.onRemoveItem?.(id);
        } else {
          this.store.clear();
        }
      }
    };

    const somethingSelected = Array.isArray(value) ? value.length > 0 : Boolean(value);

    return (
      <>
        <FormControl
          disabled={this.props.disabled}
          error={Boolean(this.props.error)}
          fullWidth={this.props.fullWidth}
          required={this.props.required}
        >
          {/*<FormLabel>Select customer</FormLabel>*/}
          <TextField
            label={this.props.label}
            InputLabelProps={{
              shrink: somethingSelected || shrink,
              error: Boolean(this.props.error)
            }}
            classes={{
              root: classes.inputRoot
            }}
            InputProps={{
              inputComponent: slim ? SlimVirtualSelectorValue : CustomerSelectorInput
            }}
            inputProps={inputProps}
            name="CustomerSelector"
            value={multi ? value : [value].filter(Boolean)}
          />

          {this.props.helperText && (
            <FormHelperText>Click to open customer selector</FormHelperText>
          )}
        </FormControl>

        {!isEmpty(this.props.value) &&
          !this.props.disabled &&
          !this.props.hideButton &&
          this.props.multi && (
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <PrimaryButton
                onClick={() => this.store.addMore(null, this.props.initialValues)}
              >
                {locale.AddMoreCustomers}
              </PrimaryButton>
            </div>
          )}
      </>
    );
  };

  handleOnShowSelected = () => {
    if (this.store.showSelected) {
      this.store.setFilter({ ...initialFilter });
    } else {
      this.store.setFilter({ includeCustomerIds: this.store.selected.map((x) => x._id) });
    }
    this.store.handleShowSelected();
  };

  render() {
    return (
      <VirtualSelector<CustomerPicker_CustomerCardFragment>
        store={this.store}
        onShowSelected={this.handleOnShowSelected}
        renderControl={() =>
          this.props.renderControl
            ? this.props.renderControl(this.handleInputClick)
            : this.defaultRenderControl()
        }
        renderItem={(data, selected) => (
          <CustomerCard
            onParentClick={() => void 0}
            onClick={() => this.store.selectItem(data)}
            data={data as Partial<Customer>}
            selected={selected}
          />
        )}
        renderFilter={() => (
          <CustomerSelectorFilter
            store={this.store}
            disabledFilterFields={this.props.disabledFilterFields}
          />
        )}
        waitingContent={
          <Typography variant="body1" style={{ fontWeight: 500, marginTop: 40 }}>
            Vänligen skriv något i sökfältet eller lägg till kundnummer för att se
            resultat.
          </Typography>
        }
      />
    );
  }
}

const styles = (theme: ThemeType) => ({
  inputRoot: {
    cursor: "pointer",

    "&$disabled": {
      cursor: "default"
    }
  },

  disabled: {}
});

export default withStyles(styles)(CustomerSelector);
